import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { JourneyApi } from "@/api/client";
import { DEFAULT_DEMO_QUESTION } from "@/lib/constants";
import { createLocalSessionId } from "@/lib/ids";
import { createSessionTicket } from "@/lib/session-ticket";
import { readDraft, saveDraft } from "./journey-draft";
import { RouteNavigation } from "./interactions/RouteNavigation";
import { JourneyRecall } from "./interactions/JourneyRecall";
import { actionKeywordFromText, cleanUserText } from "@/lib/text";
import { downloadTicketPng } from "@/lib/ticket";
import { recallCoordinateFor } from "@/ai/recall";
import { generatedJiaxingAnswer } from "@/ai/case-match";
import type { ActionResult, JiaxingCase, KnowledgeCard, QuestionAnalysis, QuestionCard, RecallCoordinate, ReflectionResult, TicketData, Topic } from "@/types/journey";
import { AudioManager } from "./audio/AudioManager";
import { EnvironmentOverlay } from "./environment/EnvironmentOverlay";
import { SceneBackground } from "./environment/SceneBackground";
import { useViewportProfile } from "./hooks/useViewportProfile";
import { WaterInput } from "./interactions/WaterInput";
import { TicketInscription } from "./interactions/TicketInscription";
import { sceneLabel, sceneOrder, type V2Scene } from "./types";
import "./styles/tokens.css";
import "./styles/experience.css";
import "./styles/cinema.css";
import "./styles/voyage.css";
import "./styles/narrative.css";

type CssVars = CSSProperties & { [key: `--${string}`]: string | number };

/** 幕间留白：让"水面正在读"的仪式感不随本地 mock 瞬时消失。 */
function pause(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** 标题行遮罩：文字从一行"水面下"整行升起，像字幕与碑文。 */
function MaskLine({ at, children }: { at: number; children: ReactNode }) {
  return (
    <span className="v2-mline">
      <span className="v2-mline-in" style={{ "--d": `${at}s` } as CssVars}>{children}</span>
    </span>
  );
}

const QUESTION_MIN_BEAT = 1250;
const REFLECTION_MIN_BEAT = 2300;

const BRAND_TITLE = "红船三问";
const CHAPTER_LABELS = ["首创", "奋斗", "奉献"] as const;

/** 终幕升腾的灯火：位置/大小/节奏都是稳定的伪随机，避免重渲染抖动。 */
const EMBER_SPECS: CssVars[] = Array.from({ length: 30 }, (_, index) => ({
  "--l": `${3 + ((index * 37 + 11) % 94)}%`,
  "--w": `${index % 5 === 0 ? 6 : index % 2 === 0 ? 4 : 3}px`,
  "--d": `${8.5 + ((index * 7) % 6)}s`,
  "--dd": `-${((index * 2.7) % 9).toFixed(1)}s`,
  "--s": `${(index % 2 === 0 ? 1 : -1) * (16 + ((index * 13) % 54))}px`,
}));

/** 三问进度：当前问、已完成问、未开始问。 */
function chapterMarks(scene: V2Scene): Array<"todo" | "current" | "done"> {
  const index = sceneOrder.indexOf(scene);
  if (index >= 5) return ["done", "done", "done"];
  return CHAPTER_LABELS.map((_, chapter) => {
    if (chapter === index - 2) return "current";
    if (chapter < index - 2) return "done";
    return "todo";
  });
}

const FIRST_CHOICES = {
  A: { title: "沿着熟悉的方向", detail: "再观察一段时间" },
  B: { title: "离开已有航线", detail: "先做一次小范围尝试" },
} as const;
const STRUGGLE_CHOICES = {
  A: { title: "先停下来", detail: "等看清方向再走" },
  B: { title: "继续向前", detail: "找出问题，再调整一次" },
} as const;
const QUESTION_THOUGHTS = ["做的事情真的有意义吗？", "没有现成答案时，还要不要试？", "努力很久没有结果怎么办？", "普通人的坚持真的会改变什么？"];
const DEDICATION_THOUGHTS = ["我愿意继续", "我会犹豫", "我还不知道"];

function splitReflection(value: string): string[] {
  const sentences = value.match(/[^。！？]+[。！？]?/g)?.map((item) => item.trim()).filter(Boolean) ?? [value];
  if (sentences.length <= 3) return sentences;
  const groups = ["", "", ""];
  sentences.forEach((sentence, index) => { groups[Math.floor(index * 3 / sentences.length)] += sentence; });
  return groups.filter(Boolean);
}

function storyBeats(item: JiaxingCase): [string, string, string] {
  const parts = item.content.match(/[^。！？]+[。！？]?/g)?.map((value) => value.trim()).filter(Boolean) ?? [];
  const groups: [string, string, string] = ["", "", ""];
  parts.forEach((part, index) => { groups[Math.floor(index * 3 / parts.length)] += part; });
  return groups;
}

function initialScene(): V2Scene {
  const value = new URLSearchParams(window.location.search).get("scene") as V2Scene | null;
  return value && sceneOrder.includes(value) ? value : "intro";
}

export function ExperienceV2() {
  const api = useMemo(() => new JourneyApi(), []);
  const audio = useMemo(() => new AudioManager(), []);
  const profile = useViewportProfile();
  const reduceMotion = useReducedMotion();
  const isDemo = new URLSearchParams(window.location.search).get("demo") === "1";

  const [restored] = useState(() => new URLSearchParams(window.location.search).has("scene") ? null : readDraft());
  const [scene, setScene] = useState<V2Scene>(() => restored?.scene ?? initialScene());
  const [previousScene, setPreviousScene] = useState<V2Scene | null>(null);
  const [transitionDir, setTransitionDir] = useState<1 | -1>(1);
  const [sessionId, setSessionId] = useState(() => restored?.sessionId ?? createLocalSessionId());
  const ticketFactory = useRef(createSessionTicket(restored?.ticket));
  const generation = useRef(0);
  const [caseReason, setCaseReason] = useState("");
  const [generatedCaseAnswer, setGeneratedCaseAnswer] = useState("");
  const [caseMethod, setCaseMethod] = useState("none");
  const [question, setQuestion] = useState(restored?.question ?? (isDemo ? DEFAULT_DEMO_QUESTION : ""));
  const [analysis, setAnalysis] = useState<QuestionAnalysis | null>(restored?.analysis ?? null);
  const [questionTwo, setQuestionTwo] = useState<QuestionCard | null>(restored?.questionTwo ?? null);
  const [firstChoice, setFirstChoice] = useState<"A" | "B" | null>(restored?.firstChoice ?? null);
  const [secondChoice, setSecondChoice] = useState<"A" | "B" | null>(restored?.secondChoice ?? null);
  const [dedication, setDedication] = useState(restored?.dedication ?? "");
  const [reflection, setReflection] = useState<ReflectionResult | null>(restored?.reflection ?? null);
  const [theoryCards, setTheoryCards] = useState<KnowledgeCard[]>([]);
  const [theoryPending, setTheoryPending] = useState(false);
  const theoryRequested = useRef(false);
  const [jiaxingCase, setJiaxingCase] = useState<JiaxingCase | null>(null);
  const [caseLoaded, setCaseLoaded] = useState(false);
  const [caseSeen, setCaseSeen] = useState(restored?.caseSeen ?? false);
  const [recallStep, setRecallStep] = useState(restored?.recallStep ?? 0);
  const [theoryError, setTheoryError] = useState(false);
  const [recallCoordinates, setRecallCoordinates] = useState<Record<number, { value: RecallCoordinate; source: string }>>({});
  const [recallPending, setRecallPending] = useState(false);
  const [recallError, setRecallError] = useState(false);
  const recallRequested = useRef<Set<number>>(new Set());
  const [ticketSaveError, setTicketSaveError] = useState(false);
  const [action, setAction] = useState(restored?.action ?? "");
  const [actionResult, setActionResult] = useState<ActionResult | null>(restored?.actionResult ?? null);
  const [ticket, setTicket] = useState<TicketData | null>(restored?.ticket ?? null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showQuestionThoughts, setShowQuestionThoughts] = useState(false);
  const [showDedicationThoughts, setShowDedicationThoughts] = useState(false);
  const [showFullReflection, setShowFullReflection] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const transitionTimer = useRef<number | null>(null);
  const restoredCaseRequested = useRef(false);
  const sessionPromise = useRef<Promise<string> | null>(null);

  function ensureSession(): Promise<string> {
    sessionPromise.current ??= api.startSession().then((value) => {
      setSessionId(value);
      return value;
    });
    return sessionPromise.current;
  }

  useEffect(() => {
    void ensureSession();
    return () => { audio.dispose(); };
  }, [api, audio]);

  useEffect(() => {
    audio.setScene(scene);
    const timer = window.setTimeout(() => mainRef.current?.querySelector<HTMLElement>(".v2-scene-layer h1")?.focus({ preventScroll: true }), reduceMotion ? 220 : 920);
    return () => window.clearTimeout(timer);
  }, [audio, reduceMotion, scene]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewportHeight = () => {
      mainRef.current?.style.setProperty("--v2-viewport-height", `${viewport?.height ?? window.innerHeight}px`);
    };
    updateViewportHeight();
    viewport?.addEventListener("resize", updateViewportHeight, { passive: true });
    window.addEventListener("orientationchange", updateViewportHeight, { passive: true });
    return () => {
      viewport?.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    if (scene !== "dedication") return;
    setShowDedicationThoughts(false);
    const timer = window.setTimeout(() => setShowDedicationThoughts(true), reduceMotion ? 120 : 2400);
    return () => window.clearTimeout(timer);
  }, [reduceMotion, scene]);

  async function loadTheory() {
    const version = generation.current;
    theoryRequested.current = true;
    setTheoryPending(true); setTheoryError(false);
    try {
      const cards = await api.publishedKnowledge(undefined, true);
      if (!Array.isArray(cards)) throw new Error("INVALID_KNOWLEDGE_RESPONSE");
      if (version === generation.current) setTheoryCards(Array.isArray(cards) ? cards : []);
    } catch { if (version === generation.current) setTheoryError(true); }
    finally { if (version === generation.current) setTheoryPending(false); }
  }
  useEffect(() => {
    if (["reflection", "theory"].includes(scene) && !theoryRequested.current) void loadTheory();
  }, [api, scene]);

  function recallAnswers(): string[] {
    return [
      firstChoice ? FIRST_CHOICES[firstChoice].title + "，" + FIRST_CHOICES[firstChoice].detail : "",
      secondChoice ? STRUGGLE_CHOICES[secondChoice].title + "，" + STRUGGLE_CHOICES[secondChoice].detail : "",
      dedication,
    ];
  }

  /**
   * 三次回望各自请一次后端 AI：把这一步的回答和对应的精神坐标放在一起写一段回望。
   * 每一步只请求一次（来回翻看不会重复调用），卡片原文只用于本地兜底。
   */
  async function loadRecallCoordinate(step: number) {
    const card = recallCoordinateFor(theoryCards, step);
    if (!card || recallRequested.current.has(step)) return;
    const version = generation.current;
    recallRequested.current.add(step);
    setRecallPending(true); setRecallError(false);
    const activeSessionId = await ensureSession();
    const [firstAnswer, secondAnswer, thirdAnswer] = recallAnswers();
    const result = await api.recall({
      sessionId: activeSessionId,
      step: step as 0 | 1 | 2,
      originalQuestion: question,
      firstAnswer: firstAnswer || "（这一步没有留下文字）",
      secondAnswer: secondAnswer || "（这一步没有留下文字）",
      thirdAnswer: thirdAnswer || "（这一步没有留下文字）",
      coordinateTitle: card.title,
      coordinateContent: card.content,
      coordinateQuote: card.display_quote ?? "",
    });
    if (version !== generation.current) return;
    setRecallCoordinates((current) => ({ ...current, [step]: result }));
    setRecallError(result.source === "local-fallback");
    setRecallPending(false);
  }

  useEffect(() => {
    if (scene !== "theory" || theoryPending || theoryError) return;
    void loadRecallCoordinate(recallStep);
  }, [scene, recallStep, theoryCards, theoryPending, theoryError]);

  useEffect(() => {
    saveDraft({ version: 1, scene, sessionId, question, analysis, questionTwo, firstChoice, secondChoice,
      dedication, reflection, action, actionResult, ticket, caseSeen, recallStep });
  }, [scene, sessionId, question, analysis, questionTwo, firstChoice, secondChoice, dedication,
    reflection, action, actionResult, ticket, caseSeen, recallStep]);

  // Published case facts are fetched again, not restored from browser storage.
  useEffect(() => {
    if (restored?.scene === "jiaxing" && !restoredCaseRequested.current) {
      restoredCaseRequested.current = true;
      void continueToJiaxing();
    }
  }, []);

  useEffect(() => {
    if (scene !== "ticket") return;
    const timer = window.setTimeout(() => audio.playSeal(), reduceMotion ? 120 : 3050);
    return () => window.clearTimeout(timer);
  }, [audio, reduceMotion, scene]);

  function showMessage(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(null), 3600);
  }

  function go(next: V2Scene, delay = 0) {
    audio.whoosh();
    setTransitionDir(sceneOrder.indexOf(next) >= sceneOrder.indexOf(scene) ? 1 : -1);
    setPulse(true);
    window.setTimeout(() => {
      setPreviousScene(scene);
      setScene(next);
      if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
      transitionTimer.current = window.setTimeout(() => { setPreviousScene(null); setPulse(false); }, reduceMotion ? 260 : 1350);
    }, reduceMotion ? Math.min(delay, 80) : delay);
  }

  function reset() {
    generation.current += 1;
    ticketFactory.current = createSessionTicket();
    sessionPromise.current = null;
    setSessionId(createLocalSessionId());
    void ensureSession();
    setBusy(false); setCaseReason(""); setGeneratedCaseAnswer(""); setCaseMethod("none");
    setCaseSeen(false); setRecallStep(0); setTheoryError(false); setTicketSaveError(false);
    setQuestion(isDemo ? DEFAULT_DEMO_QUESTION : "");
    setAnalysis(null); setQuestionTwo(null); setFirstChoice(null); setSecondChoice(null);
    setDedication(""); setReflection(null); setJiaxingCase(null); setCaseLoaded(false);
    setTheoryCards([]); theoryRequested.current = false;
    setRecallCoordinates({}); recallRequested.current = new Set(); setRecallPending(false); setRecallError(false);
    setAction(""); setActionResult(null); setTicket(null);
    setShowQuestionThoughts(false); setShowFullReflection(false); go("intro");
  }

  async function submitQuestion() {
    const safe = cleanUserText(question);
    if (busy) return;
    if (safe.length < 5 || safe.length > 20) { showMessage("把问题写在 5 到 20 个字之间，再交给红船。"); return; }
    setBusy(true); setPulse(true); audio.tap();
    const activeSessionId = await ensureSession();
    const [result] = await Promise.all([api.analyzeQuestion(activeSessionId, safe), pause(QUESTION_MIN_BEAT)]);
    setQuestion(safe); setAnalysis(result.value); setBusy(false); go("first", 420);
  }

  async function chooseFirst(value: "A" | "B") {
    if (firstChoice || busy) return;
    setFirstChoice(value); setPulse(true); setBusy(true); audio.tap();
    const activeSessionId = await ensureSession();
    const result = await api.nextQuestion(activeSessionId, analysis?.topic ?? "other", value);
    setQuestionTwo(result.value.questionTwo); setBusy(false); go("struggle", 1100);
  }

  function chooseStruggle(value: "A" | "B") {
    if (secondChoice) return;
    setSecondChoice(value); setPulse(true); audio.tap(); go("dedication", 1100);
  }

  async function submitDedication() {
    if (busy) return;
    const safe = cleanUserText(dedication);
    if (!safe) { showMessage("留下一句真实的话，哪怕只是“我会犹豫”。"); return; }
    setDedication(safe); setBusy(true); setPulse(true); audio.tap();
    const activeSessionId = await ensureSession();
    const firstAnswer = firstChoice ? FIRST_CHOICES[firstChoice].title + "，" + FIRST_CHOICES[firstChoice].detail : "";
    const secondAnswer = secondChoice ? STRUGGLE_CHOICES[secondChoice].title + "，" + STRUGGLE_CHOICES[secondChoice].detail : "";
    const request = api.reflection({ sessionId: activeSessionId, originalQuestion: question, firstAnswer, secondAnswer, thirdAnswer: safe })
      .then((result) => setReflection(result.value));
    void Promise.all([request, pause(REFLECTION_MIN_BEAT)]).finally(() => setBusy(false));
    go("reflection", 1450);
  }

  async function continueToTheory() {
    if (!reflection || busy) return;
    go("theory", 360);
  }

  async function continueToJiaxing() {
    if (busy) return;
    setBusy(true);
    const version = generation.current;
    const activeSessionId = await ensureSession();
    const matchInput = { sessionId: activeSessionId, originalQuestion: question,
      firstAnswer: firstChoice ? FIRST_CHOICES[firstChoice].title + "，" + FIRST_CHOICES[firstChoice].detail : "",
      secondAnswer: secondChoice ? STRUGGLE_CHOICES[secondChoice].title + "，" + STRUGGLE_CHOICES[secondChoice].detail : "",
      thirdAnswer: dedication, topic: reflection?.recommendedTheme ?? analysis?.topic };
    const result = await api.matchCase(matchInput);
    if (version !== generation.current) return;
    // 嘉兴答案一定要有：模型与兜底都没给出时，用本地生成的一句顶上，绝不停在空态
    const generated = result.generatedAnswer?.trim() || generatedJiaxingAnswer(matchInput);
    setJiaxingCase(result.item); setCaseReason(result.reason); setGeneratedCaseAnswer(generated); setCaseMethod(result.item ? result.method : "generated");
    setCaseSeen(Boolean(result.item || generated)); setCaseLoaded(true); setBusy(false); go("jiaxing", 320);
  }

  function createTicket(value: string) {
    const finalAction = cleanUserText(value);
    setTicket(ticketFactory.current(finalAction));
    go("ticket", 420);
  }

  async function submitAction() {
    const safe = cleanUserText(action);
    if (busy) return;
    if (!safe) { showMessage("写下一件明天能做到的小事。"); return; }
    setAction(safe); setBusy(true);
    const activeSessionId = await ensureSession();
    const result = await api.refineAction(activeSessionId, safe);
    setBusy(false);
    if (cleanUserText(result.value.refined) === safe) { createTicket(safe); return; }
    setActionResult(result.value);
  }

  async function collectTicket() {
    if (!ticket || busy) return;
    setBusy(true); setTicketSaveError(false);
    try {
      await downloadTicketPng(ticket, "red-boat-ticket.png", profile === "mobile" ? "mobile" : "desktop");
      void api.track(sessionId, "ticket_generated");
      void api.complete({ sessionId, topic: analysis?.topic ?? "other", actionKeyword: actionKeywordFromText(ticket.action), caseId: jiaxingCase?.review_status === "published" ? jiaxingCase.id : null });
    } catch {
      setTicketSaveError(true);
      return;
    } finally {
      setBusy(false);
    }
    go("lights", 520);
  }

  function sceneIndex(value: V2Scene) {
    const index = sceneOrder.indexOf(value);
    const ticketIndex = sceneOrder.indexOf("ticket");
    if (index <= ticketIndex) {
      return `${String(index + 1).padStart(2, "0")} / ${String(ticketIndex + 1).padStart(2, "0")}`;
    }
    return value.toUpperCase();
  }

  const contextOne = analysis?.questionOne.context || "当熟悉的方法不再给出答案，前方也没有可以照着走的航标。";
  const contextTwo = questionTwo?.context || "你已经为这件事走了一段路，却还没有看见明显结果。";
  const activeCase = caseLoaded ? jiaxingCase : null;

  function renderScene(): ReactNode {
    if (scene === "intro") return (
      <section className="v2-scene v2-intro" data-testid="v2-intro">
        <p className="v2-place">嘉兴 · 南湖</p>
        <div className="v2-copy">
          <div className="v2-intro-story">
            <p className="v2-in" style={{ "--d": ".45s" } as CssVars}>1921年，<br />一条船从嘉兴出发。</p>
            <p className="v2-in" style={{ "--d": "1.15s" } as CssVars}>那一代年轻人，<br />回答了他们那个时代的问题。</p>
            <p className="v2-in" style={{ "--d": "1.85s" } as CssVars}>一百多年以后，<br />问题变了。</p>
          </div>
          <h1 tabIndex={-1}><MaskLine at={2.55}>如果今天站在这里，</MaskLine><MaskLine at={2.7}>你最想问什么？</MaskLine></h1>
          <button className="v2-text-cta v2-in" type="button" onClick={() => go(ticket ? "ticket" : action ? "action" : reflection ? "theory" : secondChoice ? "dedication" : firstChoice ? "struggle" : analysis ? "first" : "question", 240)}>{analysis || dedication || action || ticket ? "继续这一程" : "靠近一点"} <span>→</span></button>
        </div>
      </section>
    );

    if (scene === "question") return (
      <section className={"v2-scene v2-question" + (busy ? " is-sending" : "")} data-testid="v2-question">
        <div className="v2-copy">
          <p className="v2-question-eyebrow v2-in">先不谈大道理。</p>
          <h1 tabIndex={-1}><MaskLine at={.42}>写下一件最近真正让你犹豫、</MaskLine><MaskLine at={.58}>困惑，或者想不明白的事。</MaskLine></h1>
          <WaterInput className="v2-in" id="v2-question-input" label="把问题留在水面上" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="我最近在想……" maxLength={20} autoFocus />
          <p className="v2-question-count" aria-live="polite">{question.trim().length}/20</p>
          <button className="v2-thought-trigger v2-in" type="button" aria-expanded={showQuestionThoughts} onClick={() => setShowQuestionThoughts((value) => !value)}>一时不知道怎么问？</button>
          {/* 桌面端常驻显示（照 V3），手机端仍由上面的开关控制展开 */}
          <div className={"v2-floating-lines" + (showQuestionThoughts ? " is-open" : "")} aria-label="别人问过的问题">
            {QUESTION_THOUGHTS.map((thought) => <button type="button" key={thought} onClick={() => setQuestion(thought)}>{thought}</button>)}
          </div>
          <button className="v2-text-cta v2-in" type="button" disabled={busy} onClick={() => void submitQuestion()}>{busy ? "红船正在听。" : "把这个问题交给红船"} <span>→</span></button>
        </div>
        {busy ? <div className="v2-question-sending" aria-hidden="true"><i /><i /><i /><span>正在水面寻找你的问题……</span></div> : null}
        <div className="v2-sent-question" aria-hidden="true">{question}</div>
      </section>
    );

    if (scene === "first") return (
      <section className="v2-scene v2-first" data-testid="v2-first">
        <div className="v2-scene-kicker v2-in">01 / 首创</div>
        <div className="v2-copy"><p className="v2-in">前面没有航标。</p>
          <h1 tabIndex={-1}><MaskLine at={.5}>你还会往<span className="v2-accent">前</span>吗？</MaskLine></h1>
          <small className="v2-in">{contextOne}</small></div>
        <RouteNavigation key="first" choices={FIRST_CHOICES} selected={firstChoice} onChoose={value => void chooseFirst(value)} />
      </section>
    );

    if (scene === "struggle") return (
      <section className="v2-scene v2-struggle" data-testid="v2-struggle">
        <div className="v2-scene-kicker v2-in">02 / 奋斗</div>
        <div className="v2-copy"><p className="v2-in">已经走了一段。</p>
          <h1 tabIndex={-1}><MaskLine at={.5}>可结果，</MaskLine><MaskLine at={.66}>还没有出现。</MaskLine></h1>
          <small className="v2-in">{contextTwo}</small></div>
        <RouteNavigation key="struggle" choices={STRUGGLE_CHOICES} selected={secondChoice} onChoose={chooseStruggle} />
      </section>
    );

    if (scene === "dedication") return (
      <section className={"v2-scene v2-dedication" + (pulse ? " is-leaving" : "")} data-testid="v2-dedication">
        <div className="v2-scene-kicker v2-in">03 / 奉献</div>
        <div className="v2-copy"><p className="v2-in">如果最后，</p>
          <h1 tabIndex={-1}><MaskLine at={.48}><span className="v2-accent">没有人知道</span>这件事</MaskLine><MaskLine at={.64}>是你做的呢？</MaskLine></h1></div>
        <WaterInput className="v2-in" id="v2-dedication-input" label="我会……" value={dedication} onChange={(event) => setDedication(event.target.value)} placeholder="把一句真实的话留在水面上" maxLength={80} />
        <div className={"v2-floating-lines" + (showDedicationThoughts ? " is-visible" : "")}>
          {DEDICATION_THOUGHTS.map((thought) => <button type="button" key={thought} onClick={() => setDedication(thought)}>{thought}</button>)}
        </div>
        <button className="v2-text-cta v2-in" type="button" disabled={!dedication.trim()} onClick={submitDedication}>留下这句话 <span>→</span></button>
        <div className="v2-answer-light" aria-hidden="true">{dedication}</div>
      </section>
    );

    if (scene === "reflection") {
      return (
        <section className="v2-scene v2-reflection" data-testid="v2-reflection">
          <div className="v2-reflection-reading">
          <div className="v2-copy"><p className="v2-in">出发时，你问——</p><blockquote>“{question}”</blockquote><h1 tabIndex={-1}>你真正反复问的，也许是——</h1></div>
          <div className={"v2-reflection-words" + (showFullReflection ? " is-complete" : "")} role="status">
            {reflection ? splitReflection(reflection.reflection).map((paragraph, index) => <p key={paragraph} style={{ animationDelay: (.35 + index * 1.3) + "s" }}>{paragraph}</p>) : <p>水面正在安静下来……</p>}
          </div>
          {reflection ? <button className="v2-read-all" type="button" onClick={() => setShowFullReflection(true)} disabled={showFullReflection}>{showFullReflection ? "回应已完整呈现" : "直接看完整回应"}</button> : null}
          </div>
          <button className="v2-text-cta v2-in" type="button" disabled={!reflection || busy} onClick={() => void continueToTheory()}>回望刚才的三次选择 <span>→</span></button>
        </section>
      );
    }

    if (scene === "theory") return <JourneyRecall
      answers={recallAnswers()} cards={theoryCards} pending={theoryPending} busy={busy} error={theoryError} onRetry={() => void loadTheory()} step={recallStep} onStep={setRecallStep} onContinue={() => void continueToJiaxing()}
      coordinate={recallCoordinates[recallStep] ?? null} aiPending={recallPending} aiError={recallError} onAiRetry={() => {
        recallRequested.current.delete(recallStep);
        void loadRecallCoordinate(recallStep);
      }} />;

    if (scene === "jiaxing") {
      const beats = activeCase ? storyBeats(activeCase) : null;
      const answer = generatedCaseAnswer.trim();
      return (
        <section className="v2-scene v2-jiaxing" data-testid="v2-jiaxing">
          <div className="v2-copy"><p className="v2-in">你的问题，</p><h1 tabIndex={-1}><MaskLine at={.38}>也有人在现实里回答。</MaskLine></h1><small className="v2-in">看看嘉兴的答案。</small></div>
          {/* 左：现实中的做法（真实案例）；右：嘉兴答案（AI 依三问写出） */}
          <div className="v2-case-columns">
            <article className="v2-case-column" data-testid="v2-real-jiaxing-case">
              <span className="v2-case-kicker">现实中的答案{activeCase?.region ? ` · ${activeCase.region}` : ""}</span>
              {beats && activeCase ? <>
                <span className="v2-case-region">{activeCase.category}</span>
                <h2 className="v2-case-title">{activeCase.title}</h2>
                <p className="v2-case-summary">{activeCase.summary}</p>
                {caseReason ? <div className="v2-case-connection"><small>{caseMethod === "keyword" ? "从共同提到的事情出发" : "与你刚才的回答相连"}</small><p>{caseReason}</p></div> : null}
                <details className="v2-source-details"><summary>看看他们怎么做的</summary>
                  <div className="v2-case-body">{beats.map((beat) => <p key={beat}>{beat}</p>)}</div>
                  <small className="v2-case-source">{activeCase.source_url ? <a href={activeCase.source_url} target="_blank" rel="noreferrer">来源：{activeCase.source_name}</a> : `来源：${activeCase.source_name}`}</small>
                </details>
              </> : <p className="v2-content-pending">{busy || !caseLoaded ? "正在寻找现实中的做法……" : "这次没有取到相近的真实案例。"}</p>}
            </article>
            <article className="v2-case-column v2-generated-answer" data-testid="v2-generated-jiaxing-answer">
              <span className="v2-case-kicker">嘉兴答案 · AI 依你的三问写出</span>
              <h2 className="v2-answer-title">给你的嘉兴回答</h2>
              <p className="v2-answer-body">{answer || "正在为你写这一段回答……"}</p>
              <small className="v2-case-source">由 AI 生成，不代表真实案例或政策事实。</small>
            </article>
          </div>
          <button className="v2-case-next" type="button" onClick={() => go("action", 360)}>写下我的明天 <span>→</span></button>
        </section>
      );
    }

    if (scene === "action") return (
      <section className="v2-scene v2-action" data-testid="v2-action">
        <div className="v2-copy"><p className="v2-in">{caseSeen ? "刚才，你看见了别人的答案。" : "你已经留下了自己的选择。"}<br />现在，只剩最后一问。</p><h1 tabIndex={-1}><MaskLine at={.46}>明天，</MaskLine><MaskLine at={.62}>你准备先做什么？</MaskLine></h1></div>
        <WaterInput className="v2-in" id="v2-action-input" label="明天我会……" value={action} onChange={(event) => { setAction(event.target.value); setActionResult(null); }} placeholder="写下一件能开始的小事" maxLength={80} />
        {actionResult ? (
          <div className="v2-refine"><p>把这件小事，<br />写得更清楚一点。</p>
            <strong>{actionResult.refined}</strong>
            <button className="v2-refine-use" type="button" onClick={() => createTicket(actionResult.refined)}>用这句话 <span>→</span></button>
            <button className="v2-refine-original" type="button" onClick={() => createTicket(action)}>还是用我的原话</button>
          </div>
        ) : null}
        {!actionResult ? <button className="v2-text-cta v2-in" type="button" disabled={busy || !action.trim()} onClick={() => void submitAction()}>
          {busy ? "正在整理这句话……" : "写下这一步"} <span>→</span>
        </button> : null}
      </section>
    );

    if (scene === "ticket") return (
      <section className="v2-scene v2-ticket" data-testid="v2-ticket" data-ticket-code={ticket?.ticketCode} data-ticket-date={ticket?.issuedDate}>
        <h1 className="v2-sr-only" tabIndex={-1}>红船青年船票 · 已启航</h1>
        {ticket ? <>
          <TicketInscription action={ticket.action} ticket={ticket} profile={profile === "mobile" ? "mobile" : "desktop"} />
        </> : null}
        <div className="v2-ticket-message"><p>把这一刻的答案，</p><strong>带到明天的生活里。</strong></div>
        <button className="v2-text-cta" type="button" disabled={!ticket || busy} onClick={() => void collectTicket()}>{busy ? "正在收好船票……" : ticketSaveError ? "重新保存船票" : "收下船票"} <span>→</span></button>
        {ticketSaveError ? <div className="v2-ticket-save-error" role="status"><p>这次没有保存成功，可以重试。</p><button type="button" disabled={busy} onClick={() => go("lights", 360)}>继续观看 →</button></div> : null}
      </section>
    );

    if (scene === "lights") return (
      <section className="v2-scene v2-lights" data-testid="v2-lights">
        <h1 className="v2-sr-only" tabIndex={-1}>LIGHTS</h1>
        <div className="v2-light-boat" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /><i /></div>
        <div className="v2-embers" aria-hidden="true">{EMBER_SPECS.map((spec, index) => <i className="v2-ember" key={index} style={spec} />)}</div>
        <button className="v2-text-cta v2-in" type="button" onClick={() => go("end", 360)}>继续 <span>→</span></button>
      </section>
    );

    return (
      <section className="v2-scene v2-end" data-testid="v2-end">
        <div className="v2-copy">
          <p className="v2-in" style={{ "--d": ".22s" } as CssVars}>一百多年前，<br />一条船从嘉兴出发。</p>
          <p className="v2-in" style={{ "--d": ".48s" } as CssVars}>今天，<br />新的答案仍在出发。</p>
          <h1 tabIndex={-1} aria-label="红船三问">{[...BRAND_TITLE].map((character, index) => <span className="v2-ch" key={index} style={{ "--i": index } as CssVars}>{character}</span>)}</h1></div>
        <div className="v2-ticket-message v2-in"><strong>AI时代的青年答案</strong></div>
        <button className="v2-text-cta v2-in" type="button" onClick={() => ticket ? go("ticket") : reset()}>查看我的船票 <span>→</span></button>
        <button className="v2-thought-trigger v2-in" type="button" onClick={reset}>再走一次</button>
      </section>
    );
  }

  return (
    <main className={"v2-experience v2-profile-" + profile + " v2-scene-" + scene} ref={mainRef}>
      <SceneBackground scene={scene} previousScene={previousScene} profile={profile} />
      <EnvironmentOverlay scene={scene} pulse={pulse} />
      <a className="v2-skip-link" href="#v2-content">跳到当前内容</a>
      <header className="v2-hud">
        <button className="v2-brand" type="button" disabled={busy || pulse} onClick={() => go("intro")} aria-label="回到开场"><span>红船三问</span><small>AI时代的青年答案</small></button>
        <div className="v2-hud-index"><span><b>{sceneIndex(scene)}</b><em>{sceneLabel[scene]}</em></span><button type="button" onClick={() => { setSoundEnabled(!soundEnabled); audio.setEnabled(!soundEnabled); }}>{soundEnabled ? "声音 开" : "声音 关"}</button></div>
      </header>
      <div className="v2-chapter-rail" aria-hidden="true">
        {chapterMarks(scene).map((mark, index) => <span key={CHAPTER_LABELS[index]} className={"is-" + mark}><i /><em>{CHAPTER_LABELS[index]}</em></span>)}
      </div>
      <div className="v2-film" aria-hidden="true"><div className="v2-grain" /><div className="v2-vignette" /></div>
      <div className="v2-boot" aria-hidden="true" />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div className="v2-scene-layer" id="v2-content" tabIndex={-1} key={scene}
          initial={{ opacity: 0, x: reduceMotion ? 0 : transitionDir * 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: reduceMotion ? 0 : transitionDir * -26 }}
          transition={{ opacity: { duration: reduceMotion ? .12 : .5 }, x: { duration: reduceMotion ? .12 : .64, ease: [0.22, 1, 0.36, 1] } }}>
          {renderScene()}
        </motion.div>
      </AnimatePresence>
      {notice ? <div className="v2-notice" role="status">{notice}</div> : null}
    </main>
  );
}
